import { Check, Circle, CircleDot, Hand, Hourglass, Minus, MousePointer, X } from "lucide-react";

import type { JobDetail } from "./jobApi";
import { Panel } from "./parts";
import { PHASE_STATE_LABEL, phaseStates, type PhaseState } from "./phaseStates";

const TONE: Record<PhaseState, string> = {
  done: "text-status-success",
  current: "text-status-running",
  awaiting_approval: "text-status-approval",
  waiting_for_person: "text-status-takeover",
  supervisor_control: "text-status-takeover",
  failed: "text-status-failed",
  stopped: "text-status-failed",
  not_reached: "text-ink-4",
  unrecorded: "text-ink-4",
};

const ICON = {
  done: Check,
  current: CircleDot,
  awaiting_approval: Hourglass,
  waiting_for_person: Hand,
  supervisor_control: MousePointer,
  failed: X,
  stopped: X,
  not_reached: Circle,
  unrecorded: Minus,
} as const;

interface Props {
  job: JobDetail;
  /** Phase of the approval waiting on this job, if any. */
  awaitingPhaseId: string | null;
}

export function PhaseList({ job, awaitingPhaseId }: Props) {
  const states = phaseStates(job, awaitingPhaseId);

  if (job.phaseDetails.length === 0) {
    return (
      <Panel title="Phases">
        <p className="text-sm text-ink-3">
          {job.phases.length > 0
            ? job.phases.join(" · ")
            : "This workflow version has no phases."}
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="Phases" aside={`${job.phaseDetails.length} total`}>
      <ol className="space-y-2">
        {job.phaseDetails.map((p, i) => {
          const state = states[i] ?? "not_reached";
          const Icon = ICON[state];
          return (
            <li key={p.id} className="flex items-start gap-3 text-sm">
              <Icon size={14} className={`mt-0.5 shrink-0 ${TONE[state]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="text-ink-1">
                  {p.order}. {p.name}
                </div>
                <div className="text-[11px] text-ink-3">
                  <span className={TONE[state]}>{PHASE_STATE_LABEL[state]}</span> · {p.riskLevel}{" "}
                  · {p.stepCount} {p.stepCount === 1 ? "step" : "steps"}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      {states.includes("unrecorded") && (
        <p className="mt-3 text-xs text-ink-3">
          No steps were recorded for this run, so per-phase progress is not known. Smoke runs
          record no steps.
        </p>
      )}
    </Panel>
  );
}
